import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { ChoiceDialog } from '@/components/ui/ChoiceDialog/ChoiceDialog';
import type { ReaderTag } from '@/api/client';
import { AddIcon, EditIcon } from '@/components/ui/Icons';
import { useToast } from '@/contexts/ToastContext';
import { ReaderCatalogTagPill } from '@/components/ReadersTable/ReaderCatalogTagPill';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { ReaderTagCreateDialog } from '@/components/ReadersTable/ReaderTagCreateDialog';
import { ReaderTagGroupRenameModal } from '@/components/ReaderTagsCatalogModal/ReaderTagGroupRenameModal';
import '@/components/ReaderTagsCatalogModal/ReaderTagsCatalogModal.css';

export type CatalogTagApi = {
  create: (data: {
    name: string;
    color: string;
    tag_group?: string;
    display_order?: number;
  }) => Promise<ReaderTag>;
  update: (
    id: string,
    data: Partial<{ name: string; color: string; tag_group: string; display_order: number }>
  ) => Promise<ReaderTag>;
  delete: (id: string) => Promise<void>;
  renameGroup: (from: string, to: string) => Promise<{ updated: number }>;
  deleteGroup: (tag_group: string) => Promise<{ deleted: number }>;
};

export interface CatalogTagsCatalogModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  queryKey: readonly unknown[];
  fetchTags: () => Promise<ReaderTag[]>;
  tagApi: CatalogTagApi;
  onInvalidateAfterMutation: (qc: QueryClient) => Promise<void>;
  groupTags: (tags: ReaderTag[]) => { group: string; tags: ReaderTag[] }[];
  catalogKind: 'reader' | 'music';
  /** Фраза после «(N) —» при удалении группы, напр. «также у всех чтецов». */
  deleteGroupScopeNote: string;
  /** Фраза в описании удаления тега, напр. «убран у всех чтецов». */
  deleteTagScopeNote: string;
}

type TagMenuSlot = { tag: ReaderTag; anchor: HTMLElement };

type GroupMenuSlot = { x: number; y: number; group: string; tagCount: number };

export function CatalogTagsCatalogModal({
  open,
  onClose,
  title,
  queryKey,
  fetchTags,
  tagApi,
  onInvalidateAfterMutation,
  groupTags,
  catalogKind,
  deleteGroupScopeNote,
  deleteTagScopeNote,
}: CatalogTagsCatalogModalProps) {
  const qc = useQueryClient();
  const { showError, showSuccess } = useToast();

  const [tagFormOpen, setTagFormOpen] = useState(false);
  const [editTag, setEditTag] = useState<ReaderTag | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReaderTag | null>(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<{ group: string; count: number } | null>(null);
  const [groupMenu, setGroupMenu] = useState<GroupMenuSlot | null>(null);
  const [groupRenameOpen, setGroupRenameOpen] = useState(false);
  const [groupRenameFrom, setGroupRenameFrom] = useState('');
  const [tagMenuActive, setTagMenuActive] = useState<TagMenuSlot | null>(null);
  const [tagMenuExiting, setTagMenuExiting] = useState<TagMenuSlot | null>(null);
  const [tagMenuExitingOpen, setTagMenuExitingOpen] = useState(false);
  const [tagMenuExitNonce, setTagMenuExitNonce] = useState(0);
  const exitingDismissRafRef = useRef<number | null>(null);

  const tagsQuery = useQuery({
    queryKey,
    queryFn: fetchTags,
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      setDeleteTarget(null);
      setDeleteGroupTarget(null);
      setGroupMenu(null);
      setGroupRenameOpen(false);
      setGroupRenameFrom('');
      setTagMenuActive(null);
      setTagMenuExiting(null);
      setTagMenuExitingOpen(false);
    }
  }, [open]);

  useEffect(
    () => () => {
      if (exitingDismissRafRef.current != null) {
        cancelAnimationFrame(exitingDismissRafRef.current);
      }
    },
    []
  );

  const handleTagMenuExitingClosed = useCallback(() => {
    setTagMenuExiting(null);
    setTagMenuExitingOpen(false);
  }, []);

  const handleTagMenuActiveClosed = useCallback(() => {
    setTagMenuActive(null);
  }, []);

  const invalidateCaches = useCallback(async () => {
    await onInvalidateAfterMutation(qc);
  }, [qc, onInvalidateAfterMutation]);

  const deleteMut = useMutation({
    mutationFn: (id: string) => tagApi.delete(id),
    onSuccess: async () => {
      showSuccess('Тег удалён');
      setDeleteTarget(null);
      await invalidateCaches();
    },
    onError: (e: Error) => showError(e.message),
  });

  const deleteGroupMut = useMutation({
    mutationFn: (tag_group: string) => tagApi.deleteGroup(tag_group),
    onSuccess: async (data) => {
      showSuccess(data.deleted > 0 ? `Удалено тегов: ${data.deleted}` : 'Группа пуста');
      setDeleteGroupTarget(null);
      await invalidateCaches();
    },
    onError: (e: Error) => showError(e.message),
  });

  const renameGroupMut = useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) => tagApi.renameGroup(from, to),
    onSuccess: async (data) => {
      showSuccess(data.updated === 0 ? 'Без изменений' : 'Группа переименована');
      setGroupRenameOpen(false);
      setGroupRenameFrom('');
      await invalidateCaches();
    },
    onError: (e: Error) => showError(e.message),
  });

  const busy =
    tagsQuery.isFetching ||
    deleteMut.isPending ||
    deleteGroupMut.isPending ||
    renameGroupMut.isPending;

  const groupedCatalog = useMemo(() => groupTags(tagsQuery.data ?? []), [groupTags, tagsQuery.data]);

  const catalogNonEmpty = groupedCatalog.some((g) => g.tags.length > 0);

  const handleAfterTagSave = useCallback(async () => {
    await invalidateCaches();
  }, [invalidateCaches]);

  const openCreateTag = useCallback(() => {
    setEditTag(null);
    setTagFormOpen(true);
  }, []);

  const openEditTag = useCallback((t: ReaderTag) => {
    setEditTag(t);
    setTagFormOpen(true);
  }, []);

  const closeTagForm = useCallback(() => {
    setTagFormOpen(false);
    setEditTag(null);
  }, []);

  const makeTagMenuItems = useCallback(
    (slot: TagMenuSlot) => [
      {
        label: 'Редактировать',
        disabled: busy,
        onClick: () => {
          setGroupMenu(null);
          openEditTag(slot.tag);
        },
      },
      {
        label: 'Удалить',
        danger: true,
        disabled: busy,
        onClick: () => {
          setGroupMenu(null);
          setDeleteTarget(slot.tag);
        },
      },
    ],
    [busy, openEditTag]
  );

  const makeGroupMenuItems = useCallback(
    (slot: GroupMenuSlot) => [
      {
        label: 'Редактировать группу',
        icon: <EditIcon size={18} />,
        disabled: busy,
        onClick: () => {
          setGroupRenameFrom(slot.group);
          setGroupRenameOpen(true);
        },
      },
      {
        label: 'Удалить группу',
        danger: true,
        disabled: busy,
        onClick: () => {
          setDeleteGroupTarget({ group: slot.group, count: slot.tagCount });
        },
      },
    ],
    [busy]
  );

  const scheduleExitingLayerClose = useCallback(() => {
    if (exitingDismissRafRef.current != null) {
      cancelAnimationFrame(exitingDismissRafRef.current);
    }
    exitingDismissRafRef.current = requestAnimationFrame(() => {
      exitingDismissRafRef.current = requestAnimationFrame(() => {
        exitingDismissRafRef.current = null;
        setTagMenuExitingOpen(false);
      });
    });
  }, []);

  const deleteGroupDescription: ReactNode = deleteGroupTarget ? (
    <>
      Будет удалена группа «<strong>{deleteGroupTarget.group}</strong>» и все теги в ней (
      <strong>{deleteGroupTarget.count}</strong>) — {deleteGroupScopeNote}
    </>
  ) : null;

  const deleteTagDescription: ReactNode = deleteTarget ? (
    <>
      Тег «<strong>{deleteTarget.name}</strong>» будет удалён из справочника и {deleteTagScopeNote}.
    </>
  ) : null;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={title}
        headerVariant="island"
        size="md"
        className="reader-tags-catalog-modal-shell"
        scrollableBody={false}
      >
        <div className="reader-tags-catalog-modal">
          <div className="reader-tags-catalog-modal__badges-wrap">
            {tagsQuery.isFetching && !catalogNonEmpty ? (
              <p className="reader-tags-catalog-modal__empty">Загрузка справочника…</p>
            ) : !catalogNonEmpty ? (
              <p className="reader-tags-catalog-modal__empty">Тегов пока нет. Добавьте первый — кнопкой внизу.</p>
            ) : (
              <OverlayScrollArea defer={false} className="reader-tags-catalog-modal__groups-scroll">
                <div className="reader-tags-catalog-modal__groups" aria-label="Теги по группам">
                  {groupedCatalog.map(({ group, tags }) =>
                    tags.length === 0 ? null : (
                      <details key={group} className="reader-tags-catalog-modal__group">
                        <summary
                          className="reader-tags-catalog-modal__group-summary"
                          title="Правый клик — действия с группой"
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setTagMenuActive(null);
                            setTagMenuExiting(null);
                            setTagMenuExitingOpen(false);
                            setGroupMenu({
                              x: e.clientX,
                              y: e.clientY,
                              group,
                              tagCount: tags.length,
                            });
                          }}
                        >
                          {group}
                        </summary>
                        <ul className="reader-tags-catalog-modal__badges" aria-label={`Теги: ${group}`}>
                          {tags.map((t) => (
                            <li key={t.id} className="reader-tags-catalog-modal__badge-item">
                              <button
                                type="button"
                                className="reader-tags-catalog-modal__tag-hit"
                                disabled={busy}
                                aria-haspopup="menu"
                                aria-expanded={tagMenuActive?.tag.id === t.id}
                                aria-label={`Действия с тегом «${t.name}»`}
                                onClick={(e) => {
                                  const anchorEl = e.currentTarget;
                                  setGroupMenu(null);
                                  setTagMenuActive((prev) => {
                                    if (prev?.tag.id === t.id) {
                                      setTagMenuExiting(null);
                                      setTagMenuExitingOpen(false);
                                      return null;
                                    }
                                    if (prev != null) {
                                      setTagMenuExitNonce((n) => n + 1);
                                      setTagMenuExiting(prev);
                                      setTagMenuExitingOpen(true);
                                      queueMicrotask(scheduleExitingLayerClose);
                                    }
                                    return { tag: t, anchor: anchorEl };
                                  });
                                }}
                              >
                                <ReaderCatalogTagPill tag={t} className="reader-catalog-tag--comfortable" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )
                  )}
                </div>
              </OverlayScrollArea>
            )}
          </div>

          <div className="reader-tags-catalog-modal__footer-stack">
            <Button
              type="button"
              variant="primary"
              size="m"
              fullWidth
              className="reader-tags-catalog-modal__add-btn"
              disabled={busy}
              onClick={openCreateTag}
            >
              <AddIcon size={20} aria-hidden />
              Добавить тег
            </Button>
            <Button type="button" variant="outlined" size="m" fullWidth onClick={onClose}>
              Закрыть
            </Button>
          </div>
        </div>
      </Modal>

      {groupMenu ? (
        <ContextMenu
          isOpen
          onClose={() => setGroupMenu(null)}
          anchorPosition={{ x: groupMenu.x, y: groupMenu.y }}
          alignY="top"
          zIndex={1120}
          items={makeGroupMenuItems(groupMenu)}
        />
      ) : null}

      {tagMenuExiting ? (
        <ContextMenu
          key={`catalog-tag-menu-exit-${tagMenuExitNonce}`}
          isOpen={tagMenuExitingOpen}
          onClose={handleTagMenuExitingClosed}
          anchorPosition={null}
          anchorEl={tagMenuExiting.anchor}
          anchorPriorityAxis="y"
          zIndex={1100}
          placementRafDepth={1}
          disableClickOutside
          menuClassName="reader-tags-catalog-modal__tag-menu-exiting"
          excludeSelector=".reader-tags-catalog-modal__tag-hit"
          items={makeTagMenuItems(tagMenuExiting)}
        />
      ) : null}

      {tagMenuActive ? (
        <ContextMenu
          isOpen
          onClose={handleTagMenuActiveClosed}
          anchorPosition={null}
          anchorEl={tagMenuActive.anchor}
          anchorPriorityAxis="y"
          zIndex={1110}
          placementRafDepth={1}
          excludeSelector=".reader-tags-catalog-modal__tag-hit"
          items={makeTagMenuItems(tagMenuActive)}
        />
      ) : null}

      <ReaderTagGroupRenameModal
        open={groupRenameOpen}
        groupName={groupRenameFrom}
        saving={renameGroupMut.isPending}
        onClose={() => {
          if (!renameGroupMut.isPending) {
            setGroupRenameOpen(false);
            setGroupRenameFrom('');
          }
        }}
        onRename={async (newName) => {
          await renameGroupMut.mutateAsync({ from: groupRenameFrom, to: newName });
        }}
      />

      <ReaderTagCreateDialog
        open={tagFormOpen}
        editTag={editTag}
        catalogKind={catalogKind}
        onClose={closeTagForm}
        onCreated={() => void handleAfterTagSave()}
        onUpdated={() => void handleAfterTagSave()}
      />

      <ChoiceDialog
        open={deleteGroupTarget !== null}
        onClose={() => setDeleteGroupTarget(null)}
        title="Удалить группу?"
        size="sm"
        description={deleteGroupDescription}
        actions={[
          {
            label: 'Отмена',
            variant: 'outlined',
            onClick: () => setDeleteGroupTarget(null),
            disabled: deleteGroupMut.isPending,
          },
          {
            label: deleteGroupMut.isPending ? 'Удаление…' : 'Удалить',
            variant: 'danger',
            disabled: deleteGroupMut.isPending || !deleteGroupTarget,
            onClick: () => {
              if (deleteGroupTarget) deleteGroupMut.mutate(deleteGroupTarget.group);
            },
          },
        ]}
      />

      <ChoiceDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Удалить тег?"
        size="sm"
        description={deleteTagDescription}
        actions={[
          {
            label: 'Отмена',
            variant: 'outlined',
            onClick: () => setDeleteTarget(null),
            disabled: deleteMut.isPending,
          },
          {
            label: deleteMut.isPending ? 'Удаление…' : 'Удалить',
            variant: 'danger',
            disabled: deleteMut.isPending || !deleteTarget,
            onClick: () => {
              if (deleteTarget) deleteMut.mutate(deleteTarget.id);
            },
          },
        ]}
      />
    </>
  );
}
